/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ConnectionKey } from '../models/ConnectionKey';
import type { DeviceResponse } from '../models/DeviceResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class HdspService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Send a XAVA message to the device.
     * Sets the next absolute position (xa) of the device, and the absolute velocity (va) the device should use to reach the position.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hdsp">Handy Direct Streaming Protocol (HDSP)</a> for additional information about the HDSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody XAVA message
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Command response.
     * @throws ApiError
     */
    public sendXava(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The absolute position to move the slider to.
             */
            xa: number;
            /**
             * The absolute velocity to use when moving the slider to the target position.
             */
            va: number;
            /**
             * Stop the device slider when the target position is reached.
             */
            stop_on_target?: boolean;
            /**
             * Immediate response. Do not wait for device response.
             */
            immediate_rsp?: boolean;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: any;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hdsp/xava',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Send a XAVP message to the device.
     * Sets the next absolute position (xa) of the device, and the percent velocity (vp) the device should use to reach the position.
     *
     * See the request schema definition for more information.
     *
     * See <a href="">Notion</a> for additional information and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody XAVP message
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Command response. In case of a device error, the response will contain an ErrorResponse object.
     * @throws ApiError
     */
    public sendXavp(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The relative position to move the slider to.
             */
            xa: number;
            /**
             * The relative velocity to use when moving the slider to the target position.
             */
            vp: number;
            /**
             * Stop the device slider when the target position is reached.
             */
            stop_on_target?: boolean;
            /**
             * Immediate response. Do not wait for device response.
             */
            immediate_rsp?: boolean;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: any;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hdsp/xavp',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Send a XPVA message to the device.
     * Sets the next percent position (xp) of the device, and the absolute velocity (va) the device should use to reach the position.
     *
     * See the request schema definition for more information.
     *
     * See <a href="">Notion</a> for additional information and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody XPVA message
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Command response. In case of a device error, the response will contain an ErrorResponse object.
     * @throws ApiError
     */
    public sendXpva(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The relative position to move the slider to.
             */
            xp: number;
            /**
             * The absolute velocity to use when moving the slider to the target position.
             */
            va: number;
            /**
             * Stop the device slider when the target position is reached.
             */
            stop_on_target?: boolean;
            /**
             * Immediate response. Do not wait for device response.
             */
            immediate_rsp?: boolean;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: any;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hdsp/xpva',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Send a XPVP message to the device.
     * Sets the next percent position (xp) of the device, and the percent velocity (vp) the device should use to reach the position.
     *
     * See the request schema definition for more information
     *
     * See <a href="">Notion</a> for additional information and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody XPVP message
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Command response. In case of a device error, the response will contain an ErrorResponse object.
     * @throws ApiError
     */
    public sendXpvp(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The relative position to move the slider to.
             */
            xp: number;
            /**
             * The relative velocity to use when moving the slider to the target position.
             */
            vp: number;
            /**
             * Stop the device slider when the target position is reached.
             */
            stop_on_target?: boolean;
            /**
             * Immediate response. Do not wait for device response.
             */
            immediate_rsp?: boolean;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: any;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hdsp/xpvp',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Send a XAT message to the device.
     * Sets the next absolute position (xa) of the device, and the time (t) the device should use to reach the position.
     *
     * See the request schema definition for more information.
     *
     * See <a href="">Notion</a> for additional information and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody XAT message
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Device response. In case of a device error, the response will contain an ErrorResponse object.
     * @throws ApiError
     */
    public sendXat(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The absolute position to move the slider to.
             */
            xa: number;
            /**
             * The time to use when moving the slider to the target position.
             */
            't': number;
            /**
             * Stop the device slider when the target position is reached.
             */
            stop_on_target?: boolean;
            /**
             * Immediate response. Do not wait for device response.
             */
            immediate_rsp?: boolean;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: any;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hdsp/xat',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Send a XAT message to the device.
     * Sets the next percent position (xp) of the device, and the time (t) the device should use to reach the position.
     *
     * See the request schema definition for more information.
     *
     * See <a href="">Notion</a> for additional information and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody XAT message
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any Command response. In case of a device error, the response will contain an ErrorResponse object.
     * @throws ApiError
     */
    public sendXpt(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The absolute position to move the slider to.
             */
            xp?: number;
            /**
             * The time to use when moving the slider to the target position.
             */
            't'?: number;
            /**
             * Stop the device slider when the target position is reached.
             */
            stop_on_target?: boolean;
            /**
             * Immediate response. Do not wait for device response.
             */
            immediate_rsp?: boolean;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: any;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hdsp/xpt',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
